import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Eyebrow, GoldRule, Reveal } from "./Reveal";

const BASE = "https://assets.bigcartel.com/theme_images";

const REALISATIONS = [
  `${BASE}/148833159/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_6_.png?auto=format&fit=max&w=800`,
  `${BASE}/148875189/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_12_.png?auto=format&fit=max&w=800`,
  `${BASE}/148875351/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_13_.png?auto=format&fit=max&w=800`,
  `${BASE}/148875861/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_14_.png?auto=format&fit=max&w=800`,
  `${BASE}/149101728/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_15_.png?auto=format&fit=max&w=800`,
  `${BASE}/149102424/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_17_.png?auto=format&fit=max&w=800`,
  `${BASE}/149102517/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_18_.png?auto=format&fit=max&w=800`,
  `${BASE}/149102616/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_19_.png?auto=format&fit=max&w=800`,
  `${BASE}/149102799/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_20_.png?auto=format&fit=max&w=800`,
  `${BASE}/149102847/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_21_.png?auto=format&fit=max&w=800`,
  `${BASE}/149156859/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_23_.png?auto=format&fit=max&w=800`,
  `${BASE}/149157612/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_27_.png?auto=format&fit=max&w=800`,
  `${BASE}/149288406/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_31_.png?auto=format&fit=max&w=800`,
  `${BASE}/149388570/Publication+Instagram+Minimaliste+Citation+en+Beige+et+Gris++_39_.png?auto=format&fit=max&w=800`,
];

const Ribbon = ({ onSelect }) => (
  <div className="flex shrink-0 gap-6 pr-6">
    {REALISATIONS.map((src, i) => (
      <div
        key={src}
        data-testid={`broderie-slide-${i}`}
        onClick={() => onSelect(src)}
        className="group relative h-72 w-56 sm:h-80 sm:w-64 shrink-0 cursor-zoom-in overflow-hidden rounded-2xl border border-[#E2DBCB]/10 bg-[#141619] transition-colors duration-300 hover:border-[#D4AF37]/40"
      >
        <img
          src={src}
          alt={`Réalisation broderie Tarantina pour salon ${i + 1}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D0E]/55 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="absolute bottom-4 left-4 rounded-full border border-[#D4AF37]/30 bg-[#0C0D0E]/60 px-3 py-1 text-[9px] font-mono uppercase tracking-widest text-[#D4AF37] opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
          Broderie Brod'Art
        </span>
      </div>
    ))}
  </div>
);

export default function Broderies({ onNavigate }) {
  const [selected, setSelected] = useState(null);
  return (
    <section
      id="broderies"
      data-testid="broderies-section"
      className="border-y border-[#E2DBCB]/10 bg-[#141619]/40 py-24 sm:py-32"
    >
      <div className="px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <Reveal>
          <Eyebrow>Réalisations</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight max-w-xl">
              Vos logos, <em className="italic text-[#D4AF37]">brodés</em> par
              nos soins.
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-[#B0ABA0]">
              Chaque salon est unique : nos clientes et clients nous confient
              leur identité, et Brod'Art la brode en Isère. Bien plus
              esthétique et durable qu'un flocage — dès 5 pièces, modèles
              mixables.
            </p>
          </div>
          <GoldRule className="mt-8" />
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div
          className="marquee-paused relative mt-14 overflow-hidden"
          data-testid="broderies-carousel"
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-24 bg-gradient-to-r from-[#0C0D0E] to-transparent md:block" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-24 bg-gradient-to-l from-[#0C0D0E] to-transparent md:block" />
          <div
            className="animate-marquee hidden md:flex w-max"
            style={{ animationDuration: "90s" }}
          >
            <Ribbon onSelect={setSelected} />
            <Ribbon onSelect={setSelected} />
          </div>
          <div
            data-lenis-prevent
            className="flex md:hidden gap-6 overflow-x-auto overscroll-x-contain px-6 pb-2 snap-x snap-mandatory"
          >
            <Ribbon onSelect={setSelected} />
          </div>
        </div>
      </Reveal>

      <div className="px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <Reveal delay={0.25}>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <button
              data-testid="broderies-cta"
              onClick={() => onNavigate("#contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-[#D4AF37] px-7 py-3.5 text-sm font-semibold text-[#0C0D0E] transition-all duration-300 hover:bg-[#C5A059] hover:gap-4"
            >
              Faire broder mon logo
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </button>
            <p className="hidden md:block text-xs font-mono uppercase tracking-[0.2em] text-[#75726B]">
              Survolez le carrousel pour le figer
            </p>
            <p className="md:hidden text-xs font-mono uppercase tracking-[0.2em] text-[#75726B]">
              Faites glisser pour défiler, touchez pour agrandir
            </p>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="broderie-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0C0D0E]/90 p-6 backdrop-blur-md"
          >
            <button
              data-testid="broderie-lightbox-close"
              onClick={() => setSelected(null)}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DBCB]/20 text-[#F7F5F0] transition-colors hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
            <motion.img
              src={selected}
              alt="Réalisation broderie Tarantina en grand"
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-2xl border border-[#E2DBCB]/15 object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
